import { ApiConflictError, ApiError, apiCall } from '@/lib/api-client';
import type { PredicateSafety } from './result-types';

/**
 * Finder run dispatch API — one endpoint for every finder kind. The server
 * picks the kind from the product: ``predicate`` (device), ``generic``
 * (ANDA drug -> Reference Listed Drug) or ``reference`` (biosimilar ->
 * originator). The 409 case includes ``detail.existing_run_id`` so the
 * caller can navigate to the in-flight result page instead of failing.
 */

export type FinderKind = 'predicate' | 'generic' | 'reference';

export interface RunDispatchResponse {
  run_id: number;
  status: string;
  // Multi-country dispatch fields (always present after migration 0023).
  // `run_id` stays as a back-compat alias for `run_ids[0]`.
  dispatch_group_id: string | null;
  run_ids: number[];
}

export interface DispatchChildRun {
  id: number;
  country: string | null;
  status: AgentRunStatus;
  started_at: string;
  completed_at: string | null;
  error_message: string | null;
  /** Which finder produced the run; titles the result page before a run loads. */
  agent_kind?: FinderKind | null;
}

export interface DispatchDetailResponse {
  dispatch_group_id: string;
  runs: DispatchChildRun[];
}

export interface InFlightConflict {
  existing_run_id: number;
}

/**
 * Dispatch a predicate run for the given product. Returns the new run's id.
 *
 * Throws `ApiConflictError` if a run is already in flight — the caller can
 * read `error.detail.existing_run_id` and navigate to its result page.
 *
 * ``country`` is the user's multi-select from the country picker, sent
 * as a comma-separated string. The backend persists it on the agent run
 * row (migration 0022). ``kind`` is kept in the signature for callsite
 * compatibility even though only ``'predicate'`` is valid today.
 */
export async function dispatchFinderRun(
  _kind: FinderKind,
  productId: number,
  country?: string[],
): Promise<RunDispatchResponse> {
  const body: { product_id: number; country?: string } = { product_id: productId };
  if (country && country.length > 0) body.country = country.join(',');
  return apiCall<RunDispatchResponse>('/api/agents/predicate/run', {
    method: 'POST',
    json: body,
  });
}

/**
 * Extract `existing_run_id` from a 409 conflict, if present.
 */
export function existingRunIdFromConflict(err: unknown): number | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = err.detail as { detail?: { existing_run_id?: unknown } } | undefined;
  const id = detail?.detail?.existing_run_id;
  return typeof id === 'number' ? id : null;
}

/**
 * Fetch a single agent run by id (used by the result page for polling).
 *
 * Mirrors backend `AgentRunDetailResponse` — the agent's JSON payload arrives
 * as a string in `output_text` and must be JSON.parsed by the renderer.
 */
export type AgentRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface AgentRunDetail {
  id: number;
  /** Customer-facing "Run #N" — per (product, agent). Null on legacy pre-backfill rows. */
  run_number?: number | null;
  product_id: number;
  /** Plucked out of ``Product.data["product_name"]`` by the GET handler so
   *  the shared ``AgentRunCard`` can label which product the run is for
   *  without a second fetch. ``null`` when the product row has no
   *  ``product_name`` key. */
  product_name: string | null;
  /** Operator provenance ("Generated by") — display name of the user who ran
   *  the run, resolved server-side. Null when unknown. */
  created_by_name: string | null;
  /** Display product type ("Medical Device" / …) for the provenance card. */
  product_type: string | null;
  agent_kind: FinderKind;
  status: AgentRunStatus;
  output_text: string | null;
  output_html: string | null;
  verification_warnings: string[];
  error_code: string | null;
  submission_type: string | null;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
  // Multi-country dispatches set this. Result page uses it to auto-switch
  // from the single-run view to the tabbed multi-country view when the
  // user arrives via ``?run_id=N`` (e.g. the in-flight "View Running"
  // button).
  dispatch_group_id: string | null;
  /** The run's own market — the stale/superseded banner's per-market scoping
   *  key (the collapsed single-run view has no tab country). */
  country: string | null;
  /** The user's chosen SE primary (run.selected_k_number). null = no
   *  explicit pick — the finder's top match is the effective default. */
  selected_k_number: string | null;
  /** US finder runs: safety history per candidate, keyed by upper-case K-number. */
  candidate_safety?: Record<string, PredicateSafety>;
  /** The dossier's effective primary has safety history nobody has confirmed yet. */
  primary_needs_safety_ack?: boolean;
}

export async function getAgentRun(runId: number): Promise<AgentRunDetail> {
  return apiCall<AgentRunDetail>(`/api/agents/runs/${runId}`, { method: 'GET' });
}

export interface PredicateSelectionResponse {
  run_id: number;
  selected_k_number: string;
}

/** Why a warned pick was confirmed — recorded in the audit row. */
export type SafetyAckContext = 'finder_pick' | 'finder_keep_default';
export type SafetyCancelContext = SafetyAckContext | 'dossier_dispatch';

/**
 * Record the user's SE-primary pick on a completed run. The backend
 * validates the K-number against the run's candidate list (422 outside it)
 * and audit-logs every change. A candidate with FDA safety history needs
 * `acknowledged` (the user confirmed the warning) or the server answers 409
 * `predicate_safety_ack_required`.
 */
export async function setPredicateSelection(
  runId: number,
  kNumber: string,
  opts?: { acknowledged: true; context: SafetyAckContext },
): Promise<PredicateSelectionResponse> {
  return apiCall<PredicateSelectionResponse>(`/api/agents/runs/${runId}/selection`, {
    method: 'POST',
    json: opts
      ? { k_number: kNumber, acknowledged: true, context: opts.context }
      : { k_number: kNumber },
  });
}

/** Log that the user saw the MAUDE/recall warning and chose Cancel. */
export async function cancelPredicateSafetyWarning(
  runId: number,
  kNumber: string,
  context: SafetyCancelContext,
): Promise<{ message: string }> {
  return apiCall<{ message: string }>(`/api/agents/runs/${runId}/safety-cancel`, {
    method: 'POST',
    json: { k_number: kNumber, context },
  });
}

/** The 409 body both server gates return when a predicate with FDA safety history
 *  hasn't been confirmed (backend/services/predicate/safety.py::ack_required_detail). */
export interface PredicateSafetyConflict {
  code: 'predicate_safety_ack_required';
  message: string;
  source: 'finder' | 'user_declared';
  predicate_run_id: number | null;
  k_number: string;
  device_name: string | null;
  safety: PredicateSafety;
  /** Present for the drug finders' picks (ANDA / 351(k) dossiers). */
  finder_kind?: 'generic' | 'reference';
}

export function predicateSafetyConflict(err: unknown): PredicateSafetyConflict | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = (err.detail as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  return (detail as { code?: unknown }).code === 'predicate_safety_ack_required'
    ? (detail as PredicateSafetyConflict)
    : null;
}

/** The 409 the dossier dispatch returns while the US primary is the finder's
 *  unconfirmed default and has no safety history
 *  (backend/services/predicate/safety.py::default_confirm_detail). */
export interface PredicateDefaultConflict {
  code: 'predicate_default_confirm_required';
  message: string;
  source: 'finder';
  predicate_run_id: number;
  k_number: string;
  device_name: string | null;
  /** Present for the drug finders' picks (ANDA / 351(k) dossiers). */
  finder_kind?: 'generic' | 'reference';
}

export function predicateDefaultConflict(err: unknown): PredicateDefaultConflict | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = (err.detail as { detail?: unknown } | undefined)?.detail;
  if (!detail || typeof detail !== 'object') return null;
  return (detail as { code?: unknown }).code === 'predicate_default_confirm_required'
    ? (detail as PredicateDefaultConflict)
    : null;
}

/**
 * Fetch every sibling run in a predicate dispatch group.
 *
 * Powers the multi-country result page's tabbed view (one tab per country).
 * Polled while at least one sibling is still ``queued`` or ``running``.
 */
export async function getPredicateDispatch(
  groupId: string,
): Promise<DispatchDetailResponse> {
  return apiCall<DispatchDetailResponse>(
    `/api/agents/predicate/dispatch/${groupId}`,
    { method: 'GET' },
  );
}

// ── Predicate re-run gate (materiality) ─────────────────────────────────────

/**
 * Extract `locked_countries` from the re-run-gate 409. Distinct shape from the
 * in-flight conflict (no ``existing_run_id``) — check this only after
 * ``existingRunIdFromConflict`` returns null. Extract to a shared home if a
 * third agent grows a gate.
 */
export function lockedCountriesFromConflict(err: unknown): string[] | null {
  if (!(err instanceof ApiConflictError)) return null;
  const detail = err.detail as { detail?: { locked_countries?: unknown } } | undefined;
  const codes = detail?.detail?.locked_countries;
  if (!Array.isArray(codes)) return null;
  const filtered = codes.filter((c): c is string => typeof c === 'string');
  return filtered.length > 0 ? filtered : null;
}

/** The classification gate's structured 422: which markets the product does not
 *  qualify for, and what classification would. */
export interface ClassificationGap {
  ineligibleMarkets: string[];
  qualifying: Record<string, string>;
  /** A drug / biologic refusal only: which pathway needs the finder, as the server words it
   *  (backend/services/finder_eligibility.classification_refusal_detail). Null for a device. */
  explanation: string | null;
}

/**
 * Extract the classification gap from a dispatch 422. Returns null for any
 * other error so the caller falls through to its generic handling.
 */
export function classificationGapFromError(err: unknown): ClassificationGap | null {
  if (!(err instanceof ApiError) || err.status !== 422) return null;
  const detail = err.detail as
    | {
        code?: unknown;
        ineligible_markets?: unknown;
        qualifying_classifications?: unknown;
        explanation?: unknown;
      }
    | undefined;
  if (detail?.code !== 'classification_not_eligible') return null;
  const markets = Array.isArray(detail.ineligible_markets)
    ? detail.ineligible_markets.filter((m): m is string => typeof m === 'string')
    : [];
  if (markets.length === 0) return null;
  const raw = detail.qualifying_classifications;
  const qualifying: Record<string, string> = {};
  if (raw && typeof raw === 'object') {
    for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v === 'string') qualifying[k] = v;
    }
  }
  const explanation =
    typeof detail.explanation === 'string' && detail.explanation ? detail.explanation : null;
  return { ineligibleMarkets: markets, qualifying, explanation };
}

/** One market's predicate dispatch availability, as reported by the freshness
 *  check (same wire shape as the strategy picker's). */
export interface PredicateMarketAvailabilityItem {
  country: string;
  available: boolean;
  reason: 'never_run' | 'product_changed' | 'up_to_date' | 'market_disabled';
  latest_run_id: number | null;
}

export interface PredicateAvailability {
  markets: PredicateMarketAvailabilityItem[];
  report_status: 'none' | 'no_baseline' | 'pending' | 'ready';
}

export async function getPredicateAvailability(
  productId: number,
): Promise<PredicateAvailability> {
  return apiCall<PredicateAvailability>(
    `/api/products/${productId}/predicate-availability`,
  );
}

/** Start the product-level freshness check (computes EVERY gated family's
 *  verdict, predicate included). Fire-and-forget; the picker polls. */
export async function triggerMaterialityCheck(productId: number): Promise<void> {
  await apiCall<{ scheduled: boolean }>(
    `/api/products/${productId}/materiality-check`,
    { method: 'POST' },
  );
}
